import type { MerchantIdentity } from '../../api/merchant'
import { avatarOf } from '../transactions/txnDisplay'
import type { Txn } from '../transactions/transactionWire'
import { useTransactionLookup } from '../transactions/useTransaction'
import type { RuleLog } from './useRuleLogs'

/**
 * One rule-page row's transaction, in the terms the rows render. The ONE seam between the rule page's
 * three tabs and where a row's transaction comes from (rv-architect I2).
 *
 * - **Applied to** reads each log's embedded `transaction` (backend 521, `logRowTxn`): no read per
 *   row. A `null` summary is `missing`.
 * - **Matches now** and **Removed** still read each transaction by id (`useRuleRowTxns`, sharing the
 *   detail screen's cache): a match carries its date and amount but no currency, and an exclusion
 *   carries neither.
 *
 * - `missing`: the transaction is no longer available (a 404, or a log's `null` summary).
 * - `unavailable`: a by-id read failed for another reason, with a retry.
 */
export type RuleRowTxn =
  | { status: 'loading' }
  | {
      status: 'found'
      id: string
      /** YYYY-MM-DD. */
      date: string
      merchant: string
      merchantId: string | null
      avatar: MerchantIdentity
      /** Signed for display, in `currency`. */
      amountCents: number
      currency: string
      /** `undefined`: not known here. A log's summary does not carry the category. */
      categoryId: string | null | undefined
    }
  | { status: 'missing' }
  | { status: 'unavailable'; retry: () => void }

/** A transaction read in full, as a row. */
export function txnRow(txn: Txn): RuleRowTxn {
  return {
    status: 'found',
    id: txn.id,
    date: txn.date,
    merchant: txn.merchant,
    merchantId: txn.merchantId,
    avatar: avatarOf(txn),
    amountCents: txn.amountCents,
    currency: txn.currency,
    categoryId: txn.categoryId,
  }
}

/**
 * An Applied to row, from its log alone. The name is the summary's display name, else the log's own
 * snapshot of the merchant text (the summary has no raw name to fall back to, as a full read would).
 */
export function logRowTxn(l: RuleLog): RuleRowTxn {
  const t = l.txn
  if (t === null) return { status: 'missing' }
  const merchant = t.merchant ?? l.merchantText ?? 'Unknown'
  return {
    status: 'found',
    id: l.transactionId,
    date: t.date,
    merchant,
    merchantId: t.merchantId,
    avatar: { id: t.merchantId ?? l.transactionId, name: merchant, type: 'other', is_global: false },
    amountCents: t.amountCents,
    currency: t.currency,
    categoryId: undefined,
  }
}

/** `row(id)` for every id a tab shows, read by id. An id that was not asked about reads as loading. */
export function useRuleRowTxns(ids: readonly string[]): (id: string) => RuleRowTxn {
  const lookup = useTransactionLookup(ids)
  return (id) => {
    const t = lookup(id)
    return t.status === 'found' ? txnRow(t.txn) : t
  }
}
